import React from "react";
import styled from "styled-components";

import {
  IChargeBasedPricingCalculation,
  ILesserOfChargesCalculation,
} from "../../types/claim";

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const Block = styled.div`
  padding: 20px;
`;

const BlockTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #333;
`;

const BlockIntro = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0 0 16px;
`;

const FormulaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormulaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
`;

const FormulaLabel = styled.div`
  color: #555;

  > span {
    color: #888;
    margin-left: 4px;
  }
`;

const FormulaAmount = styled.div`
  color: #333;
  min-width: 120px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 2px solid #333;
  padding-top: 12px;
  margin-top: 12px;
  font-size: 15px;
  font-weight: 600;
`;

// Lesser-of-charges cap note. Amber matches the cap card the standard content used to
// render, so the cap reads the same wherever the user meets it.
const CapNoteRow = styled(FormulaRow)`
  margin-top: 10px;
`;

const CapNoteLabel = styled(FormulaLabel)`
  color: #92400e;
`;

const CapNoteAmount = styled(FormulaAmount)`
  color: #92400e;
  font-weight: 600;
`;

// CLE-2600 — hierarchy-rule attribution. Muted rather than amber like the cap above: this note
// moves no money of its own, it only names the contract rule that decided what applied.
const HierarchyNoteRow = styled(FormulaRow)`
  margin-top: 10px;
`;

const HierarchyNoteLabel = styled(FormulaLabel)`
  color: #777;
  font-style: italic;
`;

// CLE-2600 — stacked view only: names what the narrowed line list is NOT showing, so the
// Total Expected below it (the whole claim) doesn't read as the sum of the rows above it.
const StackedCaption = styled.div`
  font-size: 13px;
  color: #777;
  font-style: italic;
  margin-top: 4px;
`;

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export function formatMultiplierPercent(multiplier: number): string {
  const pct = multiplier * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

/** Normalized row the LINE breakdown renders, from either the CLE-2546 or the legacy shape. */
interface ILineRow {
  cptCode: string | null;
  pricedExpectedInCents: number;
  source: "CHARGE_BASED" | "FEE_SCHEDULE" | "BUNDLED";
  multiplier: number | null;
  chargeInCents: number | null;
  maxAmountInCents?: number | null;
  capApplied: boolean;
  legacyProRated?: boolean;
  suppressedByDescription?: string | null;
}

/**
 * How this line got its number. The cap is per line item (CLE-2546), so a capped line shows the
 * ceiling that bound it rather than the old claim-level "pro-rated" note.
 *
 * `cappedByCharges` (CLE-2600) — a per-line lesser-of-charges cap bound this fee-schedule line.
 * That used to be explained by the cap card in the standard content below; this block is now the
 * only pricing view for a charge-based claim, so the note has to live on the line itself.
 */
function describeLine(line: ILineRow, cappedByCharges: boolean): string {
  if (line.source === "FEE_SCHEDULE") {
    return cappedByCharges
      ? "(fee schedule — capped at billed charges)"
      : "(fee schedule)";
  }
  // CLE-2600 — the fee schedule zeroed this line on purpose (package / case rate), so the
  // fill-the-gap rule stood down. Say why, using the bundling rule's own wording when we have it.
  if (line.source === "BUNDLED") {
    return line.suppressedByDescription
      ? `(bundled — ${line.suppressedByDescription})`
      : "(bundled — not separately reimbursed)";
  }
  if (line.legacyProRated) return "(pro-rated)";
  if (line.capApplied) {
    return line.maxAmountInCents != null
      ? `(capped at ${formatCurrency(line.maxAmountInCents)})`
      : "(capped)";
  }
  if (line.multiplier == null || line.chargeInCents == null)
    return "(% of charges)";
  return `(${String(line.multiplier)} × ${formatCurrency(line.chargeInCents)})`;
}

interface IChargeBasedPricingBlockProps {
  calculation: IChargeBasedPricingCalculation;
  /**
   * CLE-2600 — the claim's lesser-of-charges cap, when one fired. Passed only when this block
   * is the claim's ONLY pricing view (fully-owned or TOTAL_CHARGES claims, where the standard
   * content has nothing to render), which makes it the only place the cap can be explained.
   * Per-line caps annotate their line and roll up into a single summary row; a claim-total cap
   * is ignored — it has no line to annotate and cannot coexist with the per-line charge-based
   * pricing this block renders. In the stacked view (`affectedLinesOnly`) the standard
   * LesserOfChargesCard below owns the cap, so callers pass nothing here.
   */
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
  /**
   * CLE-2600 — stacked view: the standard fee-schedule content renders below this block, so
   * the block narrows to the lines the rule actually priced and leaves the fee-schedule and
   * bundled lines (and the lesser-of cap card) to the content that owns them. LINE_CHARGES
   * only; TOTAL_CHARGES has no line list, so the prop is a no-op there.
   */
  affectedLinesOnly?: boolean;
}

/**
 * Renders the Charge-Based Pricing rule audit when the rule replaces the standard
 * pricing pipeline (no fee schedule, expected = multiplier × charges). Visually
 * distinct from the lesser-of CAP card because there is no "original → capped"
 * frame: this rule IS the pricing, not a ceiling on top of one.
 */
export function ChargeBasedPricingBlock({
  calculation,
  lesserOfChargesCalculation,
  affectedLinesOnly = false,
}: IChargeBasedPricingBlockProps) {
  const {
    scope,
    multiplier,
    pricedExpectedInCents,
    rawExpectedInCents,
    maxAmountInCents,
    capApplied,
    totalChargeInCents,
    includedChargeInCents,
    perLineCaps,
    perLines,
    fullyOwned,
    hierarchyRuleName,
  } = calculation;

  const multiplierPct = formatMultiplierPercent(multiplier);
  const multiplierDecimal = String(multiplier);

  // CLE-2546 rows carry `perLines` (every line, however it was priced). Claims priced before
  // that shipped only carry `perLineCaps` (charge-based lines, pro-rated under a claim-level
  // cap), so fall back to it rather than rendering an empty breakdown for historical rows.
  const allLines: ILineRow[] =
    perLines && perLines.length > 0
      ? perLines
      : (perLineCaps ?? []).map((l) => ({
          cptCode: l.cptCode,
          pricedExpectedInCents: l.pricedExpectedInCents,
          source: "CHARGE_BASED" as const,
          multiplier,
          // Legacy rows have no per-line charge; back-computing one is only valid when no cap
          // pro-rated the value, so leave it null and let the label degrade gracefully.
          chargeInCents: capApplied
            ? null
            : Math.round(l.pricedExpectedInCents / multiplier),
          capApplied,
          legacyProRated: capApplied,
        }));

  // Stacked view: only the charge-based lines belong to this block — every other line is
  // itemized by the standard breakdown below. Legacy `perLineCaps` rows are all synthesized
  // CHARGE_BASED, so they survive the filter unchanged.
  const lines = affectedLinesOnly
    ? allLines.filter((line) => line.source === "CHARGE_BASED")
    : allLines;

  // Per-line lesser-of-charges cap only (see the prop doc). `fired` is the backend's own gate:
  // an unfired cap is not part of this claim's pricing story and stays hidden.
  const perLineChargeCap =
    lesserOfChargesCalculation?.fired &&
    lesserOfChargesCalculation.scope === "LINE_CHARGES"
      ? lesserOfChargesCalculation
      : null;
  const cappedCptCodes = new Set(
    (perLineChargeCap?.perLineCaps ?? []).map((cap) => cap.cptCode)
  );
  // The backend guarantees capped <= original; clamp anyway so a bad row can't render "−-$5.00".
  const capReductionInCents = perLineChargeCap
    ? Math.max(
        0,
        perLineChargeCap.originalExpectedInCents -
          perLineChargeCap.cappedExpectedInCents
      )
    : 0;

  // For code-filtered TOTAL rules, includedChargeInCents is the rule's denominator
  // (only participating-line charges). For wildcards it equals totalChargeInCents,
  // and we don't need the extra row.
  const showIncludedRow =
    includedChargeInCents !== totalChargeInCents && includedChargeInCents > 0;

  return (
    <>
      <TotalAllowableSection>
        <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
        <TotalAllowableAmount>
          {formatCurrency(pricedExpectedInCents)}
        </TotalAllowableAmount>
      </TotalAllowableSection>

      <Block>
        <BlockTitle>Charge-Based Pricing</BlockTitle>

        {scope === "TOTAL_CHARGES" ? (
          <>
            <BlockIntro>
              This contract prices the claim as a percentage of billed charges.
              No fee schedule, inpatient outlier, or lesser-of cap applies.
            </BlockIntro>
            <FormulaList>
              <FormulaRow>
                <FormulaLabel>Multiplier</FormulaLabel>
                <FormulaAmount>{multiplierPct}</FormulaAmount>
              </FormulaRow>
              <FormulaRow>
                <FormulaLabel>Billed Charges</FormulaLabel>
                <FormulaAmount>
                  {formatCurrency(totalChargeInCents)}
                </FormulaAmount>
              </FormulaRow>
              {showIncludedRow && (
                <FormulaRow>
                  <FormulaLabel>
                    Participating Charges
                    <span>(after Excluded Codes)</span>
                  </FormulaLabel>
                  <FormulaAmount>
                    {formatCurrency(includedChargeInCents)}
                  </FormulaAmount>
                </FormulaRow>
              )}
              {capApplied && (
                <FormulaRow>
                  <FormulaLabel>
                    Raw expected ({multiplierDecimal} ×{" "}
                    {formatCurrency(
                      showIncludedRow
                        ? includedChargeInCents
                        : totalChargeInCents
                    )}
                    )
                  </FormulaLabel>
                  <FormulaAmount>
                    {formatCurrency(rawExpectedInCents)}
                  </FormulaAmount>
                </FormulaRow>
              )}
              {maxAmountInCents != null && (
                <FormulaRow>
                  <FormulaLabel>
                    Max amount
                    <span>{capApplied ? "(applied)" : "(not reached)"}</span>
                  </FormulaLabel>
                  <FormulaAmount>
                    {formatCurrency(maxAmountInCents)}
                  </FormulaAmount>
                </FormulaRow>
              )}
              <TotalRow>
                <div>
                  {capApplied
                    ? "Expected (capped)"
                    : `Expected (${multiplierDecimal} × ${formatCurrency(
                        showIncludedRow
                          ? includedChargeInCents
                          : totalChargeInCents
                      )})`}
                </div>
                <FormulaAmount>
                  {formatCurrency(pricedExpectedInCents)}
                </FormulaAmount>
              </TotalRow>
            </FormulaList>
          </>
        ) : (
          <FormulaList>
            {!fullyOwned && (
              <BlockIntro>
                This contract prices some lines as a percentage of billed
                charges. Lines it doesn&apos;t cover keep their fee-schedule
                pricing.
              </BlockIntro>
            )}
            {lines.map((line, idx) => (
              <FormulaRow
                // eslint-disable-next-line react/no-array-index-key
                key={`cbp-line-${line.cptCode ?? "nocpt"}-${idx}`}
              >
                <FormulaLabel>
                  {line.cptCode ?? "—"}
                  <span>
                    {describeLine(
                      line,
                      line.cptCode != null && cappedCptCodes.has(line.cptCode)
                    )}
                  </span>
                </FormulaLabel>
                <FormulaAmount>
                  {formatCurrency(line.pricedExpectedInCents)}
                </FormulaAmount>
              </FormulaRow>
            ))}
            {affectedLinesOnly && (
              <StackedCaption>
                All other lines are priced by the fee schedule — see the
                breakdown below.
              </StackedCaption>
            )}
            <TotalRow>
              <div>Total Expected</div>
              <FormulaAmount>
                {formatCurrency(pricedExpectedInCents)}
              </FormulaAmount>
            </TotalRow>
            {/* Below the total on purpose: the cap is already baked into the line amounts above,
                so a row between the lines and the total would read as a second subtraction.
                Suppressed in the stacked view — the cap card in the content below owns it. */}
            {perLineChargeCap && !affectedLinesOnly && (
              <CapNoteRow>
                <CapNoteLabel>
                  Lesser-of-charges cap
                  <span>(applied per line above)</span>
                </CapNoteLabel>
                <CapNoteAmount>
                  −{formatCurrency(capReductionInCents)}
                </CapNoteAmount>
              </CapNoteRow>
            )}
          </FormulaList>
        )}

        {/* CLE-2600 — only stamped when a hierarchy directive actually changed this claim's
            pricing (a ruleset excluded, or a bundled line charge-filled), so its presence is
            itself the signal that the rule did something. */}
        {hierarchyRuleName && (
          <HierarchyNoteRow>
            <HierarchyNoteLabel>
              Hierarchy rule applied: {hierarchyRuleName}
            </HierarchyNoteLabel>
          </HierarchyNoteRow>
        )}
      </Block>
    </>
  );
}
